import React, { useState, useEffect, useCallback } from 'react';
import { SaveIcon } from './Icons';
import { savesApi, SaveStatus, SaveList, SaveListVisibility } from '../../services/api';
import { useNotifications } from '../Notifications/NotificationProvider';
import ListVisibilitySelector from './ListVisibilitySelector';

interface SaveButtonProps {
  contentType: 'TOPIC' | 'COMMENT';
  contentId: string;
  onSave?: (saved: boolean) => void;
  currentUserId?: string;
}

const SaveButton: React.FC<SaveButtonProps> = ({
  contentType,
  contentId,
  onSave,
  currentUserId
}) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>({ isSaved: false, saves: [] });
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [saveLists, setSaveLists] = useState<SaveList[]>([]);
  const [newListName, setNewListName] = useState('');
  const [selectedListId, setSelectedListId] = useState<string>('');
  const [saveNote, setSaveNote] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [newListVisibility, setNewListVisibility] = useState<SaveListVisibility>('PRIVATE_SELF');
  const [newListAudienceUserIds, setNewListAudienceUserIds] = useState<string[]>([]);
  const { notify } = useNotifications();

  const checkSaveStatus = useCallback(async () => {
    try {
      const status = await savesApi.checkSaveStatus(contentType, contentId);
      setSaveStatus(status);
    } catch (error) {
      console.error('Error checking save status:', error);
      // Set default status so button still renders even if API fails
      setSaveStatus({ isSaved: false, saves: [] });
    }
  }, [contentType, contentId]);

  const loadSaveLists = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const response = await savesApi.getLists(false);
      setSaveLists(response.lists);
    } catch (error) {
      console.error('Error loading save lists:', error);
    }
  }, [currentUserId]);

  // Check save status on mount
  useEffect(() => {
    if (currentUserId) {
      checkSaveStatus();
    } else {
      // Set default state for non-logged in users
      setSaveStatus({ isSaved: false, saves: [] });
    }
  }, [contentType, contentId, currentUserId, checkSaveStatus]);

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentUserId) {
      // Redirect to login
      const dest = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      window.location.href = dest;
      return;
    }

    if (saveStatus?.isSaved) {
      // If already saved, show options to remove or move to different list
      setShowSaveModal(true);
    } else {
      // Load save lists and show advanced modal for new saves
      await loadSaveLists();
      setShowAdvancedModal(true);
    }
  };


  const handleRemoveSave = async (saveId: string) => {
    try {
      setLoading(true);
      await savesApi.removeSave(saveId);

      notify({
        type: 'success',
        message: `${contentType.toLowerCase()} removed from saves`,
        persistent: false
      });

      await checkSaveStatus();
      onSave?.(false);
      setShowSaveModal(false);
    } catch (error: any) {
      console.error('Error removing save:', error);
      notify({
        type: 'error',
        message: `Failed to remove save: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedSave = async () => {
    if (loading) return;

    try {
      setLoading(true);

      // Create new list if specified
      let listIdToUse = selectedListId;
      if (newListName.trim()) {
        const legacy = savesApi.visibilityToLegacy(newListVisibility, newListAudienceUserIds);
        const newList = await savesApi.createList({
          name: newListName.trim(),
          description: `List for saved ${contentType.toLowerCase()}s`,
          ...legacy
        });
        listIdToUse = newList.id;

        notify({
          type: 'success',
          message: `Created new list: ${newList.name}`,
          persistent: false
        });
      }

      // Save the item
      await savesApi.saveItem({
        contentType,
        contentId,
        listId: listIdToUse || undefined,
        note: saveNote.trim() || undefined
      });

      const listName = newListName.trim() || saveLists.find(l => l.id === listIdToUse)?.name || 'Quick Saves';

      notify({
        type: 'success',
        message: `${contentType.toLowerCase()} saved to ${listName}!`,
        persistent: false
      });

      await checkSaveStatus();
      onSave?.(true);

      // Reset modal state
      setShowAdvancedModal(false);
      setNewListName('');
      setNewListVisibility('PRIVATE_SELF');
      setNewListAudienceUserIds([]);
      setSelectedListId('');
      setSaveNote('');
    } catch (error: any) {
      console.error('Error saving item:', error);
      notify({
        type: 'error',
        message: `Failed to save ${contentType.toLowerCase()}: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListAndSave = async () => {
    if (!newListName.trim()) {
      notify({
        type: 'error',
        message: 'Please enter a list name'
      });
      return;
    }

    try {
      setCreatingList(true);
      const legacy = savesApi.visibilityToLegacy(newListVisibility, newListAudienceUserIds);
      const newList = await savesApi.createList({
        name: newListName.trim(),
        description: `List for saved ${contentType.toLowerCase()}s`,
        ...legacy
      });

      await savesApi.saveItem({
        contentType,
        contentId,
        listId: newList.id,
        note: saveNote.trim() || undefined
      });

      notify({
        type: 'success',
        message: `Created "${newList.name}" and saved ${contentType.toLowerCase()}!`,
        persistent: false
      });

      await checkSaveStatus();
      onSave?.(true);

      // Reset modal state
      setShowAdvancedModal(false);
      setNewListName('');
      setNewListVisibility('PRIVATE_SELF');
      setNewListAudienceUserIds([]);
      setSaveNote('');
    } catch (error: any) {
      console.error('Error creating list and saving:', error);
      notify({
        type: 'error',
        message: `Failed to create list: ${error.response?.data?.error || error.message}`
      });
    } finally {
      setCreatingList(false);
    }
  };

  const isSaved = saveStatus?.isSaved || false;
  const saveCount = saveStatus?.saves?.length || 0;

  // Debug logging
  // Removed noisy render log

  return (
    <>
      <button
        type="button"
        className="icon-btn"
        title={
          !currentUserId
            ? "Log in to save"
            : isSaved
            ? `Saved${saveCount > 1 ? ` in ${saveCount} list${saveCount > 1 ? 's' : ''}` : ''} - click to manage`
            : `Save`
        }
        onClick={handleSaveClick}
        disabled={loading}
        style={{
          color: isSaved ? '#059669' : undefined,
          backgroundColor: isSaved ? '#ecfdf5' : undefined,
          border: isSaved ? '1px solid #a7f3d0' : undefined
        }}
      >
        <SaveIcon size={14} />
        {saveCount > 1 && (
          <span style={{
            fontSize: '10px',
            marginLeft: '2px',
            fontWeight: 'bold'
          }}>
            {saveCount}
          </span>
        )}
      </button>

      {/* Save Management Modal */}
      {showSaveModal && saveStatus && (
        <div
          className="save-modal-overlay"
          onClick={() => setShowSaveModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            className="save-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              minWidth: '300px',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Manage Save
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>
                This {contentType.toLowerCase()} is saved in:
              </p>

              {saveStatus.saves.map((save) => (
                <div
                  key={save.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    border: '1px solid #e5e5e5',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}
                >
                  <span style={{ fontSize: '14px' }}>
                    {save.list ? save.list.name : 'Quick Saves'}
                  </span>
                  <button
                    onClick={() => handleRemoveSave(save.id)}
                    disabled={loading}
                    style={{
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={async () => {
                  setShowSaveModal(false);
                  await loadSaveLists();
                  setShowAdvancedModal(true);
                }}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Add to List
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Save Modal */}
      {showAdvancedModal && (
        <div
          className="save-modal-overlay"
          onClick={() => setShowAdvancedModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            className="save-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              Save {contentType.toLowerCase()}
            </h3>

            {/* Quick Save Option */}
            <div style={{ marginBottom: '20px' }}>
              <button
                onClick={() => {
                  setSelectedListId('');
                  handleAdvancedSave();
                }}
                disabled={loading}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginBottom: '16px'
                }}
              >
                Quick Save (no list)
              </button>
            </div>

            {/* Existing Lists */}
            {saveLists.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                  Save to existing list:
                </label>
                <select
                  value={selectedListId}
                  onChange={(e) => {
                    setSelectedListId(e.target.value);
                    // Clear new list name when selecting existing list
                    if (e.target.value) {
                      setNewListName('');
                    }
                  }}
                  disabled={!!newListName.trim()}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    opacity: newListName.trim() ? 0.5 : 1,
                    cursor: newListName.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">Choose a list...</option>
                  {saveLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({list._count?.saves || 0} items)
                    </option>
                  ))}
                </select>
                {newListName.trim() && (
                  <small style={{ display: 'block', color: '#6b7280', marginTop: '4px' }}>
                    Clear the new list name below to select an existing list
                  </small>
                )}
              </div>
            )}

            {/* Create New List */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Or create new list:
              </label>
              <input
                type="text"
                value={newListName}
                onChange={(e) => {
                  setNewListName(e.target.value);
                  // Clear selected list when typing new list name
                  if (e.target.value.trim()) {
                    setSelectedListId('');
                  }
                }}
                disabled={!!selectedListId}
                placeholder={selectedListId ? "Clear selection above to create new list" : "Enter list name..."}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  opacity: selectedListId ? 0.5 : 1,
                  cursor: selectedListId ? 'not-allowed' : 'text'
                }}
              />

              {/* Privacy Settings for New List */}
              {newListName.trim() && (
                <div style={{ marginTop: '12px' }}>
                  <ListVisibilitySelector
                    visibility={newListVisibility}
                    audienceUserIds={newListAudienceUserIds}
                    onVisibilityChange={setNewListVisibility}
                    onAudienceChange={setNewListAudienceUserIds}
                    showDescription={false}
                  />
                </div>
              )}
            </div>

            {/* Optional Note */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                Add note (optional):
              </label>
              <textarea
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                placeholder="Add a personal note..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAdvancedModal(false);
                  setNewListName('');
                  setNewListVisibility('PRIVATE_SELF');
                  setNewListAudienceUserIds([]);
                  setSelectedListId('');
                  setSaveNote('');
                }}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              {newListName.trim() ? (
                <button
                  onClick={handleCreateListAndSave}
                  disabled={loading || creatingList}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    opacity: loading || creatingList ? 0.6 : 1
                  }}
                >
                  {creatingList ? 'Creating...' : 'Create List & Save'}
                </button>
              ) : (
                <button
                  onClick={handleAdvancedSave}
                  disabled={loading || (!selectedListId && !newListName.trim())}
                  style={{
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    opacity: loading || (!selectedListId && !newListName.trim()) ? 0.6 : 1
                  }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SaveButton;
